import React, { useState } from 'react'
import {
  Text,
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from 'react-native'
import { ProjectBackIcon, WhitePlus } from '../../../../assets/icons'
import { BtText } from '@linqpal/components/src/ui/BtText'
import { paths } from '../../../links'
import { useTranslation } from 'react-i18next'
import { routes2 } from '@linqpal/models'
import Loading from '../../../Loading'
import { ProjectsTable } from '../../../Projects/Desktop/Components/ProjectsTable'
import { useResponsive } from '@linqpal/components/src/hooks'
import { composeStyle } from '@linqpal/common-frontend/src/helpers'
import projectsStore from '../../../Projects/Store'
import InvoiceActionsStore from '../InvoiceActionsStore'

const { isSearchScoreMoreThanThreshold } = projectsStore

const Suggested: React.FC<{ matchScore: number; t: any }> = ({
  matchScore,
  t,
}) => {
  const { sm } = useResponsive()
  return isSearchScoreMoreThanThreshold(matchScore) ? (
    <View style={sm ? styles.suggested : styles.suggestedMobile}>
      <Text
        style={{
          fontWeight: '600',
          fontSize: 12,
          color: '#00A0F3',
        }}
      >
        {t('SelectProject.suggested')}
      </Text>
    </View>
  ) : null
}

export const Table = ({ route, navigation }) => {
  const { resumeApplyingForLoan } = InvoiceActionsStore
  const { t } = useTranslation('global')
  const { sm } = useResponsive()
  const [selectedProjectId, setSelectedProjectId] = useState('')

  const onSelect = (item) => {
    const invoices = route.params?.ids.split(',')
    setSelectedProjectId(item._id)
    routes2.project
      .addInvoices(item._id, { invoices })
      .then(() => {
        console.log('Invoice linked to project')
        navigation.goBack()
        resumeApplyingForLoan()
      })
      .catch((err) => {
        console.log(err)
        setSelectedProjectId('')
      })
  }

  const getAddress = ({ streetAddress, city, state, zip }) => {
    return `${streetAddress}, ${city}, ${state}, ${zip}`
  }

  const col = [
    {
      cell: (item: any) => (
        <View
          style={{
            marginBottom: 20,
            marginTop: 20,
            width: '100%',
          }}
        >
          {!sm && <Suggested t={t} matchScore={item.matchScore} />}
          <View
            style={{
              flexDirection: 'row',
            }}
          >
            <View style={{ flexDirection: 'row', flex: 1 }}>
              <BtText
                style={composeStyle(
                  styles.rowsLabel,
                  !sm && styles.rowsLabelMobile,
                )}
              >
                {item.data
                  ? getAddress(item.data?.propertyAddress.items[0].content)
                  : item.address}
              </BtText>
              {sm && <Suggested t={t} matchScore={item.matchScore} />}
            </View>
            <TouchableOpacity
              style={{
                width: sm ? 126 : 84,
                height: 34,
                borderRadius: 8,
                backgroundColor: isSearchScoreMoreThanThreshold(item.matchScore)
                  ? '#00A0F3'
                  : '#F5F7F8',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onPress={() => onSelect(item)}
            >
              {selectedProjectId === item._id ? (
                <Loading />
              ) : (
                <BtText
                  style={{
                    fontWeight: '700',
                    fontSize: 14,
                    color: isSearchScoreMoreThanThreshold(item.matchScore)
                      ? 'white'
                      : '#003353',
                  }}
                >
                  {t('SelectProject.select-button-label')}
                </BtText>
              )}
            </TouchableOpacity>
          </View>
        </View>
      ),
    },
  ]

  return <ProjectsTable cols={col} params={route.params?.ids} />
}

export const DesktopScreen = (props) => {
  const { t } = useTranslation('global')
  const { navigation, route } = props

  return (
    <ScrollView style={styles.wrapper}>
      <TouchableOpacity
        testID="projects-detail-back"
        onPress={() => {
          navigation.goBack()
        }}
        style={styles.backBtnContainer}
      >
        <ProjectBackIcon />
        <BtText style={styles.backBtnText}>
          {t('SelectProject.back-to-invoice-details')}
        </BtText>
      </TouchableOpacity>
      <Text style={styles.title}>{t('SelectProject.title')}</Text>
      <Text style={styles.subtitle}>{t('SelectProject.subtitle')}</Text>
      <TouchableOpacity
        style={styles.button}
        activeOpacity={0.8}
        onPress={() => {
          navigation.navigate(paths.Console.Payables.NewProject, {
            ids: route.params?.ids,
          })
        }}
        testID="projects-add-button"
      >
        <BtText style={styles.addButtonLabel}>
          {t('Projects.add_new_project')}
        </BtText>
        <WhitePlus width={16} height={16} />
      </TouchableOpacity>
      <Table {...props} />
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  wrapper: {
    maxWidth: 680,
    alignSelf: 'center',
  },
  title: {
    fontWeight: '700',
    fontSize: 32,
    lineHeight: 40,
    marginBottom: 10,
  },
  subtitle: {
    fontWeight: '500',
    fontSize: 18,
    lineHeight: 28,
    marginBottom: 20,
    color: '#19262F',
  },
  button: {
    border: 'none',
    borderRadius: 4,
    backgroundColor: '#00A0F3',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 16,
    maxWidth: 220,
    maxHeight: 48,
    marginTop: 15,
    marginBottom: 40,
  },
  addButtonLabel: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 24,
    marginRight: 24,
  },
  rowsLabel: {
    fontWeight: '500',
    fontSize: 14,
    color: '#003353',
    lineHeight: 20,
  },
  rowsLabelMobile: {
    maxWidth: 260,
  },
  suggested: {
    height: 22,
    width: 129,
    backgroundColor: '#F4F9FD',
    paddingHorizontal: 10,
    borderRadius: 5,
    marginLeft: 10,
    justifyContent: 'center',
  },
  suggestedMobile: {
    height: 22,
    width: 129,
    backgroundColor: '#F4F9FD',
    paddingHorizontal: 10,
    borderRadius: 5,
    justifyContent: 'center',
    marginBottom: 10,
  },
  backBtnContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 70,
    marginBottom: 20,
  },
  backBtnText: {
    fontWeight: '600',
    color: '#335C75',
    fontSize: 16,
    marginLeft: 15,
  },
})
